/* The "?" shortcuts sheet (Tier-1 A5) — the keyboard canon in one table,
 * opened with ? anywhere in the app, closed with Esc, the ✕ or a click on
 * the scrim. A dialog: focus lands inside and returns to where it was.
 *
 * ONLY bindings that work today are listed — a row goes in when its key
 * is wired, never before (the card verbs A/R/S/E and the drawer's ‹ ›
 * are not, so they are not here). */

import { useEffect, useRef, useState } from 'react'
import { isTypingTarget } from './useListKeys'

const ROWS: { ctx: string; keys: string[][]; does: string[] }[] = [
  {
    ctx: 'Any list',
    keys: [['J', 'K'], ['Enter'], ['Esc']],
    does: [
      'move the cursor',
      'opens the object',
      'closes what you opened and returns focus to the row',
    ],
  },
  {
    ctx: 'Drawer',
    keys: [['Esc'], ['Tab']],
    does: ['closes, focus back on the row', 'cycles inside the drawer'],
  },
  {
    ctx: 'Task page',
    keys: [['Esc']],
    does: ['back to its queue, the row re-selected'],
  },
  {
    ctx: 'Anywhere',
    keys: [['?']],
    does: ['this sheet'],
  },
]

export default function ShortcutsSheet() {
  const [open, setOpen] = useState(false)
  const panel = useRef<HTMLDivElement | null>(null)
  const opener = useRef<Element | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === '?' &&
        !open &&
        !isTypingTarget(e.target) &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    if (!open) return
    opener.current = document.activeElement
    panel.current?.focus()
    // Esc closes the sheet and nothing else: innermost first.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      setOpen(false)
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      const back = opener.current
      if (back instanceof HTMLElement && document.contains(back)) back.focus()
    }
  }, [open])

  if (!open) return null
  return (
    <>
      <div className="kb-scrim" data-testid="shortcuts-scrim" onClick={() => setOpen(false)} />
      <div
        ref={panel}
        className="kb-sheet"
        role="dialog"
        aria-label="Keyboard shortcuts"
        tabIndex={-1}
        data-testid="shortcuts-sheet"
      >
        <div className="kb-head">
          <span className="kb-title">Keyboard</span>
          <span className="kb-sub">one model for every list, drawer and card</span>
          <button
            type="button"
            className="kb-close"
            aria-label="Close shortcuts"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>
        </div>
        <table className="kb-table">
          <thead>
            <tr>
              <th>Context</th>
              <th>Key</th>
              <th>Does</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.ctx}>
                <td className="kb-ctx">{r.ctx}</td>
                <td className="kb-keys">
                  {r.keys.map((line, i) => (
                    <div key={i}>
                      {line.map((k) => (
                        <kbd key={k}>{k}</kbd>
                      ))}
                    </div>
                  ))}
                </td>
                <td>
                  {r.does.map((d, i) => (
                    <div key={i}>{d}</div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Not a key — but the one switch the sheet must name: the help dots
            and where they are turned on and off (audit cut 3). */}
        <p className="kb-note" data-testid="shortcuts-note">
          The ? beside a title is a help dot: it explains the card or the queue. <b>Explain</b>, the
          switch in the rail under the nav, shows or hides every help dot.
        </p>
      </div>
    </>
  )
}
